import React, { useState, useEffect } from 'react';
import { liquidityThresholdAPI } from '../../../../services';
import './LiquidityThresholdManager.css';

const LiquidityThresholdManager = () => {
  const [threshold, setThreshold] = useState(null);
  const [source, setSource] = useState(null);
  const [isDynamic, setIsDynamic] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [newThreshold, setNewThreshold] = useState('');

  // Fetch current threshold on mount
  const fetchThreshold = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await liquidityThresholdAPI.get();
      const data = response.data;

      if (data.success && data.data && data.data.data) {
        const t = data.data.data;
        setThreshold(t.threshold);
        setSource(t.source);
        setIsDynamic(t.isDynamic);
        setNewThreshold(t.threshold.toString());
      } else {
        setError(data.data?.error || 'Failed to load threshold');
      }
    } catch (err) {
      const msg = err.response?.data?.details || err.response?.data?.error || err.message;
      setError(msg || 'Failed to connect to engine');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchThreshold();
  }, []);

  // Set a new dynamic threshold
  const handleSet = async () => {
    const value = parseFloat(newThreshold);
    if (isNaN(value) || value <= 1 || value > 100) {
      setError('Threshold must be a number between 1 and 100');
      setSuccessMessage(null);
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);
      const response = await liquidityThresholdAPI.set(value);
      const data = response.data;

      if (data.success && data.data && data.data.data) {
        const t = data.data.data;
        setThreshold(t.threshold);
        setSource('admin-api');
        setIsDynamic(true);
        setSuccessMessage(`Liquidation threshold updated to ${t.threshold}%`);
      } else {
        setError(data.data?.error || data.data?.details || 'Failed to set threshold');
      }
    } catch (err) {
      const msg = err.response?.data?.details || err.response?.data?.error || err.message;
      setError(msg || 'Failed to set threshold');
      setSuccessMessage(null);
    } finally {
      setSaving(false);
    }
  };

  // Reset to .env value
  const handleReset = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);
      const response = await liquidityThresholdAPI.reset();
      const data = response.data;

      if (data.success && data.data && data.data.data) {
        const t = data.data.data;
        setThreshold(t.threshold);
        setSource('environment-variable');
        setIsDynamic(false);
        setNewThreshold(t.threshold.toString());
        setSuccessMessage(`Threshold reset to environment value: ${t.threshold}%`);
      } else {
        setError(data.data?.error || data.data?.details || 'Failed to reset threshold');
      }
    } catch (err) {
      const msg = err.response?.data?.details || err.response?.data?.error || err.message;
      setError(msg || 'Failed to reset threshold');
      setSuccessMessage(null);
    } finally {
      setSaving(false);
    }
  };

  // Clear messages after 5 seconds
  useEffect(() => {
    if (successMessage || error) {
      const timer = setTimeout(() => {
        // Only clear success (not errors)
        setSuccessMessage(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, error]);

  const getThresholdColor = (val) => {
    if (val == null) return '#636E72';
    if (val <= 10) return '#D63031'; // Red: very aggressive
    if (val <= 25) return '#E17055'; // Orange: aggressive
    if (val <= 50) return '#FDCB6E'; // Yellow: moderate
    return '#00B894'; // Green: conservative
  };

  const getThresholdLabel = (val) => {
    if (val == null) return 'Unknown';
    if (val <= 10) return 'Aggressive';
    if (val <= 25) return 'Moderate';
    if (val <= 50) return 'Conservative';
    return 'Very Conservative';
  };

  return (
    <div className="lt-container">
      {/* Header */}
      <div className="lt-header">
        <div>
          <h2>Liquidity Threshold</h2>
          <p className="lt-subtitle">
            Configure the margin level at which user positions are automatically liquidated
          </p>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="lt-error-banner">
          <i className="fas fa-exclamation-circle"></i>
          <span>{error}</span>
          <button onClick={() => setError(null)}>
            <i className="fas fa-times"></i>
          </button>
        </div>
      )}

      {/* Success banner */}
      {successMessage && (
        <div className="lt-success-banner">
          <i className="fas fa-check-circle"></i>
          <span>{successMessage}</span>
        </div>
      )}

      {/* Loading state */}
      {loading ? (
        <div className="lt-loading">
          <div className="lt-spinner"></div>
          <p>Loading threshold configuration...</p>
        </div>
      ) : (
        <div className="lt-content">
          {/* Current Status Card */}
          <div className="lt-card lt-status-card">
            <div className="lt-card-header">
              <h3>Current Threshold</h3>
              <span className={`lt-badge ${isDynamic ? 'lt-badge-dynamic' : 'lt-badge-env'}`}>
                {isDynamic ? 'Dynamic (Admin)' : 'Environment (.env)'}
              </span>
            </div>

            <div className="lt-status-body">
              {/* Large threshold display */}
              <div className="lt-threshold-display">
                <div 
                  className="lt-threshold-gauge"
                  style={{ borderColor: getThresholdColor(threshold) }}
                >
                  <span 
                    className="lt-threshold-value"
                    style={{ color: getThresholdColor(threshold) }}
                  >
                    {threshold ?? '—'}%
                  </span>
                  <span 
                    className="lt-threshold-label"
                    style={{ color: getThresholdColor(threshold) }}
                  >
                    {getThresholdLabel(threshold)}
                  </span>
                </div>

                <div className="lt-threshold-explanation">
                  <p>
                    <strong>What does this mean?</strong>
                  </p>
                  <p>
                    When a user's <em>Margin Level</em> (Equity / Used Margin × 100) drops
                    below <strong>{threshold ?? '—'}%</strong>, all of their open positions
                    will be automatically liquidated to prevent further losses.
                  </p>
                  <div className="lt-scale">
                    <div className="lt-scale-item">
                      <span className="lt-scale-dot" style={{ background: '#D63031' }}></span>
                      <span>1-10% — Aggressive liquidation</span>
                    </div>
                    <div className="lt-scale-item">
                      <span className="lt-scale-dot" style={{ background: '#E17055' }}></span>
                      <span>11-25% — Moderate protection</span>
                    </div>
                    <div className="lt-scale-item">
                      <span className="lt-scale-dot" style={{ background: '#FDCB6E' }}></span>
                      <span>26-50% — Conservative protection</span>
                    </div>
                    <div className="lt-scale-item">
                      <span className="lt-scale-dot" style={{ background: '#00B894' }}></span>
                      <span>51-100% — Maximum protection</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Update Card */}
          <div className="lt-card lt-update-card">
            <div className="lt-card-header">
              <h3>
                <i className="fas fa-sliders-h"></i>
                Update Threshold
              </h3>
              <p>Set a new dynamic threshold (takes effect immediately, overrides .env value)</p>
            </div>

            <div className="lt-update-body">
              <div className="lt-input-group">
                <label htmlFor="threshold-input">Threshold Value (%)</label>
                <div className="lt-input-row">
                  <input
                    id="threshold-input"
                    type="number"
                    className="lt-input"
                    placeholder="e.g. 25"
                    min="1"
                    max="100"
                    step="1"
                    value={newThreshold}
                    onChange={(e) => setNewThreshold(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSet()}
                  />
                  <button
                    className="lt-btn lt-btn-primary"
                    onClick={handleSet}
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <i className="fas fa-spinner fa-spin"></i>
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-check"></i>
                        Apply
                      </>
                    )}
                  </button>
                </div>
                <p className="lt-input-hint">
                  Valid range: 2–100. Example: 25 means liquidate when equity falls below 25% of used margin.
                </p>
              </div>

              {/* Reset section */}
              <div className="lt-reset-section">
                <div className="lt-divider"></div>
                <div className="lt-reset-body">
                  <div>
                    <h4>Reset to Environment Default</h4>
                    <p>
                      Revert to the value defined in the trade-engine's <code>.env</code> file
                      (<code>LIQUIDATION_THRESHOLD</code>). This clears the dynamic override.
                    </p>
                  </div>
                  <button
                    className="lt-btn lt-btn-outline"
                    onClick={handleReset}
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <i className="fas fa-spinner fa-spin"></i>
                        Resetting...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-undo"></i>
                        Reset to .env
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Info Card */}
          <div className="lt-card lt-info-card">
            <div className="lt-card-header">
              <h3>
                <i className="fas fa-info-circle"></i>
                How It Works
              </h3>
            </div>
            <div className="lt-info-body">
              <div className="lt-info-steps">
                <div className="lt-step">
                  <div className="lt-step-number">1</div>
                  <div>
                    <strong>Margin Level Calculation</strong>
                    <p>The engine continuously recalculates each user's margin level: Equity ÷ Used Margin × 100</p>
                  </div>
                </div>
                <div className="lt-step">
                  <div className="lt-step-number">2</div>
                  <div>
                    <strong>Threshold Check</strong>
                    <p>If margin level drops to or below the threshold, the user is flagged for liquidation</p>
                  </div>
                </div>
                <div className="lt-step">
                  <div className="lt-step-number">3</div>
                  <div>
                    <strong>Auto-Liquidation</strong>
                    <p>All open positions are closed via market orders sent to the liquidity provider</p>
                  </div>
                </div>
                <div className="lt-step">
                  <div className="lt-step-number">4</div>
                  <div>
                    <strong>Reset on Recovery</strong>
                    <p>If the user deposits more funds or market prices recover above the threshold, the liquidation flag is cleared</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LiquidityThresholdManager;
